import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Lock, ShieldCheck } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { PageHeader, useTenant } from "@/components/AppShell";
import { Badge, Button, Card, cx, Skeleton } from "@/components/ui";
import { api, ApiError, type ValidationSettingsView } from "@/lib/api";
import { msg, t } from "@/lib/i18n";

const LEVELS = [["error", msg("hält zurück")], ["warning", msg("nur Hinweis")], ["off", msg("aus")]] as const;
type Level = (typeof LEVELS)[number][0];
const CATEGORY: Record<string, string> = { identification: msg("Kennzeichnung"), logistics: msg("Logistik"), customs: msg("Zoll"), handling: msg("Handhabung"), bundle: msg("Sets & Gebinde"), content: msg("Inhalt") };

/**
 * Stammdaten-Prüfung je Kunde: welche Prüfregeln einen Artikel sperren (er geht nicht ans Lager, Aufträge mit ihm werden
 * zurückgehalten). Kunden sehen die Einstellung; ändern darf nur Logiful.
 */
export function ValidationSettingsPage() {
  const { tenantId } = useTenant();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["validation-settings", tenantId], queryFn: () => api.validationSettings.get(tenantId) });
  const [draft, setDraft] = useState<{ shipsOutsideEu: boolean; rules: Record<string, Level> } | null>(null);
  const init = (d: ValidationSettingsView) => ({ shipsOutsideEu: d.shipsOutsideEu, rules: Object.fromEntries(d.rules.filter((r) => r.configurable).map((r) => [r.id, (r.level === "info" ? "warning" : r.level) as Level])) });
  useEffect(() => { if (q.data && !draft) setDraft(init(q.data)); }, [q.data, draft]);
  const save = useMutation({
    mutationFn: () => api.validationSettings.save(tenantId, draft!),
    onSuccess: (d) => { qc.setQueryData(["validation-settings", tenantId], d); setDraft(init(d)); toast.success(t("Gespeichert"), { description: t("Alle Artikel werden neu geprüft – was jetzt durchgeht, geht ans Lager, wartende Aufträge laufen weiter.") }); },
    onError: (e) => toast.error(e instanceof ApiError ? ((e.body as { message?: string })?.message ?? t("Fehler")) : t("Fehler")),
  });
  const d = q.data;
  const editable = !!d?.editable;
  const dirty = !!d && !!draft && JSON.stringify(draft) !== JSON.stringify(init(d));
  const groups = d ? [...new Set(d.rules.map((r) => r.category))] : [];

  return (
    <div className="pb-16">
      <PageHeader title={t("Stammdaten-Prüfung")} description={t("Welche fehlenden oder fehlerhaften Angaben einen Artikel sperren: Er geht dann nicht ans Lager, und Aufträge mit ihm werden zurückgehalten.")} />
      <div className="px-4 md:px-8 space-y-4 max-w-3xl">
        {!d || !draft ? <Skeleton className="h-60" /> : (
          <>
            {!editable && (
              <Card className="p-4 text-[12.5px] text-fg-muted flex gap-2"><Lock className="size-4 shrink-0 mt-0.5" /><span>{t("Diese Einstellung nimmt Logiful für euch vor. Sprecht uns an, wenn eine Angabe bei euch nicht zurückhalten soll.")}</span></Card>
            )}
            <Card className="p-5">
              <label className={cx("flex items-start gap-3", editable && "cursor-pointer")}>
                <input type="checkbox" className="mt-1 accent-ink" disabled={!editable} checked={draft.shipsOutsideEu} onChange={(e) => setDraft({ ...draft, shipsOutsideEu: e.target.checked })} />
                <span>
                  <span className="font-medium text-[13.5px]">{t("Zolldaten prüfen (Versand in Drittländer)")}</span>
                  <span className="block text-[12.5px] text-fg-muted">{t("Zolltarifnummer, Ursprungsland und Warenwert werden geprüft. Aus: Zolldaten werden gar nicht verlangt.")}</span>
                </span>
              </label>
            </Card>
            {groups.map((g) => (
              <Card key={g} className="overflow-hidden">
                <h2 className="px-5 pt-4 pb-2 font-semibold text-[14px]">{t(CATEGORY[g] ?? g)}</h2>
                <ul>
                  {d.rules.filter((r) => r.category === g).map((r) => {
                    const current = r.configurable ? draft.rules[r.id]! : r.level === "info" ? "warning" : r.level;
                    return (
                      <li key={r.id} className="flex flex-wrap items-center gap-2 px-5 py-2.5 border-t border-border text-[13px]">
                        <span className="flex-1 min-w-48">
                          {t(r.description)}
                          {r.configurable && current !== (r.defaultLevel === "info" ? "warning" : r.defaultLevel) && <Badge className="ml-2">{t("angepasst")}</Badge>}
                        </span>
                        {r.configurable ? (
                          <div className="inline-flex p-0.5 rounded-lg bg-surface-2 border border-border" role="radiogroup" aria-label={t(r.description)}>
                            {LEVELS.map(([lv, label]) => (
                              <button key={lv} type="button" role="radio" aria-checked={current === lv} disabled={!editable} onClick={() => setDraft({ ...draft, rules: { ...draft.rules, [r.id]: lv } })} className={cx("h-7 px-2.5 rounded-md text-[12px] font-medium disabled:cursor-default", current === lv ? (lv === "error" ? "bg-surface shadow-sm text-danger" : "bg-surface shadow-sm") : "text-fg-muted hover:text-fg")}>
                                {t(label)}
                              </button>
                            ))}
                          </div>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-[12px] text-fg-subtle" title={t("Braucht das Lager zwingend – nicht einstellbar")}><Lock className="size-3" /> {current === "error" ? t("hält zurück") : t("nur Hinweis")}</span>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </Card>
            ))}
            {editable && (
              <div className="flex justify-end gap-2">
                {dirty && <Button variant="ghost" onClick={() => setDraft(init(d))}>{t("Verwerfen")}</Button>}
                <Button icon={<ShieldCheck className="size-4" />} disabled={!dirty} loading={save.isPending} onClick={() => save.mutate()}>{t("Speichern")}</Button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
